import {
  Stack,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Divider,
  useMediaQuery,
  useTheme,
  Skeleton,
  Tooltip,
} from "@mui/material";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import ViewReport from "assets/icons1/View_Report_Icon.svg";
import { useAppSelector } from "utils/hooks";
import { selectUserName } from "redux/authSlice";
import { useEffect, useState } from "react";
import { fetchCountsName, fetchLastExecutedCampaign } from "./DashboardService";
import {
  formatDateWithTimeInLongDateFormat,
  getFormattedStatsCount,
  handleErrorMessage,
} from "helpers/methods";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import SubscriberOverview from "./Components/SubscriberOverview";
import { campaignTypesEnum } from "screens/Campaigns/Constants";
import email_icon from "assets/icons1/email_icon.svg";
import CreateCampaign from "screens/Campaigns/CreateCampaign/CreateCampaign";

const Dashboard = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const userName = useAppSelector(selectUserName);
  const [lastCampaign, setLastCampaign] = useState<any>();
  const [countNames, setCountNames] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [createCampaignModalVisibility, setCreateCampaignModalVisibility] =
    useState<boolean>(false);

  useEffect(() => {
    getLastExecutedCampaign();
  }, []);

  const getLastExecutedCampaign = async () => {
    try {
      setIsLoading(true);
      const countName = await fetchCountsName();
      const response = await fetchLastExecutedCampaign();
      setCountNames(countName);
      setLastCampaign(response);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const getPercentage = (count: number, divideby: number) => {
    return ((count / (divideby === 0 ? 1 : divideby)) * 100).toFixed();
  };

  const getLastCampaign = () => {
    const openedCount =
      lastCampaign?.metrics?.find(
        (m: any) => m.metricId === countNames["Opened"]
      )?.count || 0;
    const clickedCount =
      lastCampaign?.metrics?.find(
        (m: any) => m.metricId === countNames["Clicked"]
      )?.count || 0;
    const recipientsCount =
      lastCampaign?.metrics?.find(
        (m: any) => m.metricId === countNames["Requested"]
      )?.count || 0;
    const successCount =
      lastCampaign?.metrics?.find(
        (m: any) => m.metricId === countNames["Success"]
      )?.count || 0;

    return (
      <>
        <Stack direction="column" spacing={1} px={2}>
          <Stack direction="column" spacing={1} pb={2}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h3" color="text.primary">
                Welcome, {userName}!
              </Typography>
              <Button
                variant="contained"
                onClick={() => setCreateCampaignModalVisibility(true)}
              >
                Create Campaign
              </Button>
            </Stack>

            <Typography variant="body1" fontWeight={500} color="text.primary">
              Here's your recent activities
            </Typography>
          </Stack>
          <Card>
            <CardContent>
              <Stack>
                {lastCampaign?.id !== 0 ? (
                  <Stack
                    direction={isDesktop ? "row" : "column"}
                    spacing={3}
                    justifyContent="space-between"
                  >
                    <Stack flex={1} justifyContent="space-between">
                      <Stack spacing={0.5}>
                        <Typography
                          variant="body2"
                          gutterBottom
                          fontWeight={600}
                        >
                          Last Sent Campaign
                        </Typography>
                        <Tooltip title={lastCampaign?.name} arrow>
                          <Typography
                            gutterBottom
                            variant="h6"
                            noWrap
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                            maxWidth="400px"
                            display="block"
                          >
                            {lastCampaign?.name}
                          </Typography>
                        </Tooltip>
                        <Typography variant="body2" color="text.secondary">
                          Created by {lastCampaign?.formEmail}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {campaignTypesEnum[lastCampaign?.type]} Campaign •
                          Sent on{" "}
                          {formatDateWithTimeInLongDateFormat(
                            lastCampaign?.scheduleTime
                          )}
                        </Typography>
                      </Stack>
                      <Box pt={2}>
                        <Button
                          id="export_csv_btn"
                          variant="text"
                          size="small"
                          startIcon={
                            <Stack>
                              <img src={ViewReport} alt="Export CSV" />
                            </Stack>
                          }
                          sx={{
                            padding: "0px !important",
                            paddingTop: "8px !important",
                            "& .MuiButton-startIcon": {
                              marginRight: "2px",
                            },
                          }}
                          onClick={() =>
                            history.push(
                              `${viewpaths.CampaignStatsViewPath}?id=${lastCampaign?.id}`
                            )
                          }
                        >
                          View Report
                        </Button>
                      </Box>
                    </Stack>
                    <Stack
                      direction={isDesktop ? "row" : "column"}
                      spacing={3}
                      alignItems={isDesktop ? "center" : "flex-start"}
                    >
                      {[
                        {
                          label: "Recipients",
                          key: "Success",
                          value: getFormattedStatsCount(recipientsCount),
                          infoMessage:
                            "The total number of recipients who received this campaign.",
                        },
                        {
                          label: "Opened",
                          key: "Opened",
                          value: `${getPercentage(openedCount, successCount)}%`,
                          infoMessage:
                            "The percentage of unique recipients who opened the email.",
                        },
                        {
                          label: "Clicked",
                          key: "Clicked",
                          value: `${getPercentage(
                            clickedCount,
                            successCount
                          )}%`,
                          infoMessage:
                            "The percentage of recipients who clicked on at least one link within the email.",
                        },
                        {
                          label: "CTOR",
                          key: "ctor",
                          value: `${getPercentage(clickedCount, openedCount)}%`,
                          infoMessage:
                            "The percentage of recipients who opened the email and clicked on a link.",
                        },
                      ].map((stat, index) => (
                        <>
                          <Tooltip
                            title={stat.infoMessage}
                            placement="bottom"
                            arrow
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  width: "auto",
                                  background: theme.palette.primary.dark,
                                  fontSize: "12px",
                                  fontFamily: "Inter, sans-serif",
                                },
                              },
                              arrow: {
                                sx: {
                                  "&::before": {
                                    backgroundColor: theme.palette.primary.dark,
                                  },
                                },
                              },
                            }}
                          >
                            <Stack
                              key={index}
                              textAlign="center"
                              alignItems="center"
                              spacing={1}
                            >
                              <Typography variant="h5" color="primary">
                                {stat.value}
                              </Typography>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                <Typography variant="body2">
                                  {stat.label}
                                </Typography>
                              </Stack>
                            </Stack>
                          </Tooltip>

                          {stat.key !== "ctor" && (
                            <Divider
                              orientation={
                                isDesktop ? "vertical" : "horizontal"
                              }
                              flexItem={isDesktop ? false : true}
                              sx={{ height: "45%" }}
                            />
                          )}
                        </>
                      ))}
                    </Stack>
                  </Stack>
                ) : (
                  <Stack direction="column" spacing={1}>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      color="text.secondary"
                      textAlign="center"
                    >
                      Create your first campaign to bloom your business😎!
                    </Typography>
                    <Typography
                      variant="h6"
                      textAlign="center"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                      onClick={() => setCreateCampaignModalVisibility(true)}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img src={email_icon} />{" "}
                      </span>{" "}
                      Create Campaign
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </>
    );
  };

  const LastCampaignSkeleton = () => {
    return (
      <Stack direction="column" spacing={1} px={2}>
        <Stack direction="column" spacing={1} pb={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h4" color="text.primary">
              <Skeleton width={200} />
            </Typography>
            <Skeleton variant="rounded" width={200} height={40} />
          </Stack>
          <Typography variant="body1" fontWeight={500} color="text.primary">
            <Skeleton width={300} />
          </Typography>
        </Stack>
        <Card>
          <CardContent>
            <Stack
              direction={isDesktop ? "row" : "column"}
              spacing={3}
              justifyContent="space-between"
            >
              <Stack flex={1} justifyContent="space-between">
                <Stack spacing={0.5}>
                  <Typography variant="body2" gutterBottom fontWeight={600}>
                    <Skeleton width={150} />
                  </Typography>
                  <Typography
                    variant="h4"
                    color="text.secondary"
                    fontWeight={700}
                  >
                    <Skeleton width={250} />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <Skeleton width={200} />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <Skeleton width={280} />
                  </Typography>
                </Stack>

                <Box pt={2}>
                  <Button
                    variant="text"
                    size="small"
                    sx={{
                      padding: "0px !important",
                      paddingTop: "8px !important",
                    }}
                    disabled
                  >
                    <Skeleton width={100} height={30} />
                  </Button>
                </Box>
              </Stack>
              <Stack
                direction={isDesktop ? "row" : "column"}
                spacing={3}
                alignItems={isDesktop ? "center" : "flex-start"}
              >
                {["Recipients", "Opened", "Clicked"].map((label, index) => (
                  <>
                    <Stack textAlign="center">
                      <Typography variant="h5" color="primary">
                        <Skeleton width={50} />
                      </Typography>
                      <Typography variant="body2" pt={1}>
                        <Skeleton width={80} />
                      </Typography>
                    </Stack>
                    {label !== "Clicked" && (
                      <Divider
                        orientation={isDesktop ? "vertical" : "horizontal"}
                        flexItem={isDesktop ? false : true}
                        sx={{ height: "45%" }}
                      />
                    )}
                  </>
                ))}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  };

  const getCreateCampaignModal = () => {
    return (
      <CreateCampaign
        isDialogOpen={createCampaignModalVisibility}
        handleDialogClose={() => setCreateCampaignModalVisibility(false)}
      />
    );
  };

  const getPerformanceOverViewLoader = () => {
    return (
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h4" color="text.primary">
          <Skeleton width={200} />
        </Typography>
      </Box>
    );
  };

  const getBody = () => {
    return (
      <Stack direction="column" spacing={3} px={2}>
        {isLoading ? (
          getPerformanceOverViewLoader()
        ) : (
          <Box
            display="flex"
            alignItems="cehnter"
            justifyContent="space-between"
          >
            <Typography variant="h4" color="text.primary">
              Performance Overview
            </Typography>
          </Box>
        )}
        <SubscriberOverview />
        {createCampaignModalVisibility && getCreateCampaignModal()}
      </Stack>
    );
  };

  const getPage = () => {
    return (
      <Stack spacing={3}>
        {isLoading ? LastCampaignSkeleton() : getLastCampaign()}
        {getBody()}
      </Stack>
    );
  };

  return <CenteredAuthenticatedLayout>{getPage()}</CenteredAuthenticatedLayout>;
};

export default Dashboard;
